import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import {
  getCategories,
  languages,
  getSubCategoriesByParentCategory,
  getVideos,
} from ".";
import { CircularProgress, TableContainer, TablePagination } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import Paper from "@mui/material/Paper";
import TableHead from "@mui/material/TableHead";
import {StyledTableCell,StyledTableRow} from '../../utils/constantData'
import { AdminPageLayout, AdminSectionCard } from "../../components/admin/AdminPageLayout";

export default function Videos() {
  const [data, setData] = React.useState([]);
  const [subCategory, SetSubCategory] = React.useState([]);
  const [lang, setLang] = React.useState("English");
  const [id, setId] = React.useState(1);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [videoCount, setVideoCount] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  
  const [localCategory, setLocalCategory] = React.useState("Agriculture");
  const [filterKey, setFilterKey] = React.useState(0);
  const startIndex = page * rowsPerPage;

  const hasActiveFilters = lang !== "English" || localCategory !== "Agriculture";

  React.useEffect(() => {
    async function fetchVideos() {
      setLoading(true);
      setVideoCount([]);

      if (!subCategory.length || !localCategory) {
        setLoading(false);
        return;
      }

      try {
        const counts = await Promise.all(
          subCategory.map(async (item) => {
            const response = await getVideos(item.name, lang, localCategory);
            const videos = Array.isArray(response.data?.videos)
              ? response.data.videos
              : [];
            return { name: item.name, key: videos.length };
          })
        );
        setVideoCount(counts);
      } catch (error) {
        console.log(error);
        setVideoCount([]);
      } finally {
        setLoading(false);
      }
    }
    fetchVideos();
  }, [subCategory, lang, localCategory]);

  // function to handle categorry change and api of state
  const handleCategory = (value) => {
    if (value) {
      setLocalCategory(value.name);
      setId(value.id);
    } else {
      SetSubCategory([]);
      setId("");
      setLocalCategory("");
    }
  };

  // function to handle categorry change and api of state
  const handleLanguage = (value) => {
    if (value) setLang(value.name);
    else {
      SetSubCategory([]);
      setId("");
    }
  };

  const fetchData = async () => {
    try {
      const response = await getCategories();
      const responseData = response.data;
      setData(responseData?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchsubCategory = async () => {
    try {
      const response = await getSubCategoriesByParentCategory(
        id
      );
      const responseData = response.data;
      SetSubCategory(responseData?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    if (id !== "") fetchsubCategory();
    setLoading(true);
  }, [id, lang, localCategory]);

  React.useEffect(() => {
    fetchData();
  }, [lang]);

  // function to handle changes to the number of rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // function to handle changes to the current page:
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <AdminPageLayout
      eyebrow="Insights"
      title="Video Analysis"
      description="See how many videos exist under each sub category with a consistent analytics table and faster filters."
      actions={
        <div className="admin-toolbar">
          <Autocomplete
            key={`lang-${filterKey}`}
            disablePortal
            id="language"
            options={languages}
            defaultValue={{ id: 1, name: "English" }}
            getOptionLabel={(option) => option.name}
            onChange={(event, newValue) => handleLanguage(newValue)}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            sx={{ width: 220 }}
            renderInput={(params) => <TextField {...params} label="Language" />}
          />
          <Autocomplete
            key={`cat-${filterKey}`}
            disablePortal
            id="category"
            options={data}
            defaultValue={{ id: 1, name: "Agriculture" }}
            getOptionLabel={(option) => option.name}
            onChange={(event, newValue) => handleCategory(newValue)}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            sx={{ width: 220 }}
            renderInput={(params) => <TextField {...params} label="Category" />}
          />
          {/* <Button
            id="reset-filters-btn"
            variant="outlined"
            startIcon={<RestartAlt />}
            disabled={!hasActiveFilters}
            onClick={() => {
              setLang("English");
              setLocalCategory("Agriculture");
              setId(1);
              SetSubCategory([]);
              setPage(0);
              setFilterKey((k) => k + 1);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              whiteSpace: "nowrap",
              borderColor: hasActiveFilters ? "rgba(63,122,42,0.4)" : undefined,
              color: hasActiveFilters ? "#3f7a2a" : undefined,
              "&:hover": hasActiveFilters ? { borderColor: "#3f7a2a", background: "rgba(63,122,42,0.06)" } : {},
            }}
          >
            Reset
          </Button> */}
        </div>
      }
    >
      <div className="admin-kpi-grid">
        <div className="admin-kpi-card">
          <strong>{data.length}</strong>
          <span>Available categories</span>
        </div>
        <div className="admin-kpi-card">
          <strong>{subCategory.length}</strong>
          <span>Mapped sub categories</span>
        </div>
        <div className="admin-kpi-card">
          <strong>{videoCount.reduce((sum, item) => sum + (item.key || 0), 0)}</strong>
          <span>Total videos in current view</span>
        </div>
      </div>
      <AdminSectionCard
        title="Sub category distribution"
        description="Each row shows the number of uploaded videos available inside a sub category."
        className="admin-table-shell"
      >
      <TableContainer component={Paper} className="mt-0">
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead style={{position:'sticky',top:0}}>
            <StyledTableCell>Sr. No.</StyledTableCell>
            <StyledTableCell>Sub Category</StyledTableCell>
            <StyledTableCell>Video Count</StyledTableCell>
          </TableHead>
          <TableBody>
            {videoCount.length != 0 ? (
              videoCount
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <StyledTableRow key={row.name || index}>
                    <StyledTableCell component="th">
                       {startIndex + index + 1}
                    </StyledTableCell>
                    <StyledTableCell>{row.name}</StyledTableCell>
                    <StyledTableCell>{row.key}</StyledTableCell>
                  </StyledTableRow>
                ))
            ) :loading?   <tr><td colSpan={3} className="text-center py-8"><CircularProgress size={20}  color="success" className="mt-5 me-2" />Loading....</td></tr> :(
              <tr><td colSpan={3} className="text-center py-8">No Data Found</td></tr>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={videoCount.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </AdminSectionCard>
    </AdminPageLayout>
  );
}
